import type { AnatomyBinding, AnatomySize } from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

const xButtonGeometry: readonly AnatomyBinding[] = [
    { partId: "xButton", property: "width", literal: "20px" },
    { partId: "xButton", property: "height", literal: "20px" },
    { partId: "xButton", property: "border-radius", token: "--radius-s" },
    {
        partId: "xButton",
        property: "color",
        literal: "currentColor",
        note: "The close glyph inherits the tag foreground.",
    },
];

const sizeGeometry = ({
    height,
    paddingX,
    radius,
    iconSize,
    text,
}: {
    height: string;
    paddingX: AnatomyBinding;
    radius: `--${string}`;
    iconSize: string;
    text: readonly AnatomyBinding[];
}): readonly AnatomyBinding[] => [
    {
        partId: "background",
        property: "height",
        literal: height,
        note: "Figma geometry, not a spacing token.",
    },
    paddingX,
    { partId: "background", property: "border-radius", token: radius },
    { partId: "background", property: "gap", token: "--space-4" },
    {
        partId: "glyph",
        property: "width",
        literal: iconSize,
        note: "Leading svg size for this t-shirt.",
    },
    ...text,
];

const tokenPad = (token: `--${string}`): AnatomyBinding => ({
    partId: "background",
    property: "padding-inline",
    token,
    via: "--tag-padding-x",
});

export const tagAnatomySizes: readonly AnatomySize[] = [
    {
        id: "lg",
        label: "lg",
        selector: ".sizeLg",
        bindings: [
            ...sizeGeometry({
                height: "28px",
                paddingX: tokenPad("--space-8"),
                radius: "--radius-m",
                iconSize: "12px",
                text: anatomyTextStyle({
                    scale: "s",
                    weight: "500",
                    mixin: "text-s-14-medium",
                }),
            }),
            {
                partId: "background",
                property: "padding-right (with X)",
                token: "--space-4",
                note: "Only when the tag has an X button.",
            },
            ...xButtonGeometry,
        ],
    },
    {
        id: "md",
        label: "md",
        selector: ".sizeMd",
        bindings: [
            ...sizeGeometry({
                height: "24px",
                paddingX: tokenPad("--space-8"),
                radius: "--radius-m",
                iconSize: "12px",
                text: anatomyTextStyle({
                    scale: "xs",
                    weight: "500",
                    mixin: "text-xs-12-medium",
                }),
            }),
            {
                partId: "background",
                property: "padding-right (with X)",
                token: "--space-2",
                note: "Only when the tag has an X button.",
            },
            ...xButtonGeometry,
        ],
    },
    {
        id: "sm",
        label: "sm",
        selector: ".sizeSm",
        bindings: [
            ...sizeGeometry({
                height: "20px",
                paddingX: tokenPad("--space-6"),
                radius: "--radius-s",
                iconSize: "10px",
                text: anatomyTextStyle({
                    scale: "2xs",
                    weight: "500",
                    mixin: "text-2xs-10-medium",
                }),
            }),
            {
                partId: "background",
                property: "padding-inline (numeric)",
                literal: "6.5px",
                note: "ds-deviation: Figma Number padX at 20px; no half-pixel spacing token.",
            },
        ],
    },
    {
        id: "xs",
        label: "xs",
        selector: ".sizeXs",
        bindings: [
            {
                partId: "background",
                property: "height",
                literal: "16px",
                note: "Figma geometry, not a spacing token.",
            },
            {
                partId: "background",
                property: "padding-inline",
                literal: "4.5px",
                note: "ds-deviation: Figma Number padX at 16px; no half-pixel spacing token.",
            },
            {
                partId: "background",
                property: "border-radius",
                token: "--radius-s",
            },
            {
                partId: "background",
                property: "min-width",
                literal: "16px",
                note: "Square at one digit.",
            },
            ...anatomyTextStyle({
                scale: "2xs",
                weight: "500",
                mixin: "text-2xs-10-medium",
            }),
        ],
    },
];
